import React, { useState, useEffect } from 'react';
import { collection, query, getDocs, where, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../context/UserContext';
import Swal from 'sweetalert2';
import UserNav from '../../components/navbar/user-nav';
import Breadcrumb from '../../components/Breadcrumb';

interface MissingUser {
  correo: string;
  nombre: string;
  nombreCompleto: string;
  tipo: 'asociado' | 'asesor';
  telefono: string;
  sitioWeb: string;
  agenciaNombre: string;
  inmobiliariaDocId: string | null;
  certificaciones: string[];
  redes: {
    facebook: string;
    instagram: string;
  };
  // Para usuarios ya en Auth pero sin doc
  authUid?: string;
}

interface CSVRow {
  nombre: string;
  tipo: string;
  nombreCompleto: string;
  agencia: string;
  correoOriginal: string;
  correo: string;
  telefono: string;
  sitioWeb: string;
  certificaciones: string;
  redes?: string;
  activo?: string;
  seccion?: string;
}

interface Inmobiliaria {
  id: string;
  nombre: string;
  nombreNormalizado: string;
}

const FixMissingFirestoreUsers: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useUser();

  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [csvData, setCsvData] = useState<CSVRow[]>([]);
  const [missingUsers, setMissingUsers] = useState<MissingUser[]>([]);
  const [inmobiliarias, setInmobiliarias] = useState<Inmobiliaria[]>([]);
  const [creationResults, setCreationResults] = useState<{ correo: string; success: boolean; message: string }[]>([]);
  const [progress, setProgress] = useState({ current: 0, total: 0 });

  // Cargar inmobiliarias al inicio
  useEffect(() => {
    const fetchInmobiliarias = async () => {
      try {
        const snap = await getDocs(collection(db, 'inmobiliaria'));
        const data = snap.docs.map(doc => ({
          id: doc.id,
          nombre: doc.data().nombre || '',
          nombreNormalizado: normalizeString(doc.data().nombre || '')
        }));
        setInmobiliarias(data);
        if (process.env.NODE_ENV === "development") console.log(`✅ ${data.length} inmobiliarias cargadas`);
      } catch (error) {
        console.error('Error cargando inmobiliarias:', error);
      }
    };
    fetchInmobiliarias();
  }, []);

  // Normalizar string para comparación
  const normalizeString = (str: string): string => {
    return str
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]/g, '')
      .trim();
  };

  // Buscar inmobiliaria por nombre
  const findInmobiliariaDocId = (agenciaNombre: string): string | null => {
    if (!agenciaNombre) return null;

    const normalizado = normalizeString(agenciaNombre);

    // Búsqueda exacta
    let found = inmobiliarias.find(i => i.nombreNormalizado === normalizado);
    if (found) return found.id;

    // Búsqueda parcial
    found = inmobiliarias.find(i =>
      i.nombreNormalizado.includes(normalizado) ||
      normalizado.includes(i.nombreNormalizado)
    );
    if (found) return found.id;

    return null;
  };

  // Parsear CSV (formato: miembros-ampi - miembros-ampi-procesado.csv)
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const lines = text.split('\n');

      // Headers esperados del CSV procesado:
      // nombre, tipo, nombreCompleto, agencia, correoOriginal, correo, telefono, sitioWeb, certificaciones, col10, col11, col12, activo, seccion
      const data: CSVRow[] = [];

      for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;

        // Manejar campos con comas dentro de comillas
        const values: string[] = [];
        let current = '';
        let inQuotes = false;

        for (const char of line) {
          if (char === '"') {
            inQuotes = !inQuotes;
          } else if (char === ',' && !inQuotes) {
            values.push(current.trim());
            current = '';
          } else {
            current += char;
          }
        }
        values.push(current.trim());

        if (values.length >= 6) {
          data.push({
            nombre: values[0] || '',
            tipo: values[1] || '',
            nombreCompleto: values[2] || '',
            agencia: values[3] || '',
            correoOriginal: values[4] || '',
            correo: values[5] || '',
            telefono: values[6] || '',
            sitioWeb: values[7] || '',
            certificaciones: values[8] || '',
            activo: values[12] || '1',
            seccion: values[13] || ''
          });
        }
      }

      setCsvData(data);
      if (process.env.NODE_ENV === "development") console.log(`📄 CSV cargado: ${data.length} registros`);

      Swal.fire({
        icon: 'success',
        title: 'CSV Cargado',
        text: `Se encontraron ${data.length} registros`,
        confirmButtonColor: '#3085d6'
      });
    };
    reader.readAsText(file);
  };

  // Analizar usuarios que están en CSV pero no en Firestore
  const analyzeUsers = async () => {
    if (csvData.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Sin datos',
        text: 'Por favor carga el archivo CSV primero (miembros-ampi - miembros-ampi-procesado.csv)',
        confirmButtonColor: '#3085d6'
      });
      return;
    }

    setAnalyzing(true);
    setMissingUsers([]);
    const missing: MissingUser[] = [];

    try {
      // Obtener todos los correos existentes en Firestore
      const [asociadosSnap, asesoresSnap] = await Promise.all([
        getDocs(collection(db, 'asociado')),
        getDocs(collection(db, 'asesor'))
      ]);

      const correosExistentes = new Set<string>();

      asociadosSnap.docs.forEach(doc => {
        const correo = doc.data().correo?.toLowerCase().trim();
        if (correo) correosExistentes.add(correo);
      });

      asesoresSnap.docs.forEach(doc => {
        const correo = doc.data().correo?.toLowerCase().trim();
        if (correo) correosExistentes.add(correo);
      });

      if (process.env.NODE_ENV === "development") console.log(`📊 Correos en Firestore: ${correosExistentes.size}`);

      // Buscar usuarios del CSV que no están en Firestore
      for (const row of csvData) {
        const correo = row.correo?.toLowerCase().trim();
        if (!correo || !correo.includes('@')) continue;

        if (!correosExistentes.has(correo)) {
          const tipo = row.tipo?.toLowerCase() === 'afiliado' ? 'asesor' : 'asociado';
          const inmobiliariaDocId = findInmobiliariaDocId(row.agencia);

          // Parsear certificaciones
          let certificaciones: string[] = [];
          if (row.certificaciones && row.certificaciones !== 'N/A') {
            certificaciones = row.certificaciones
              .split(',')
              .map(c => c.trim())
              .filter(c => c.length > 0);
          }

          missing.push({
            correo,
            nombre: row.nombre,
            nombreCompleto: row.nombreCompleto || row.nombre,
            tipo,
            telefono: row.telefono || '',
            sitioWeb: row.sitioWeb || '',
            agenciaNombre: row.agencia,
            inmobiliariaDocId,
            certificaciones,
            redes: {
              facebook: '',
              instagram: ''
            }
          });
        }
      }

      setMissingUsers(missing);

      Swal.fire({
        icon: missing.length > 0 ? 'info' : 'success',
        title: 'Análisis completado',
        html: `
          <p><strong>Total en CSV:</strong> ${csvData.length}</p>
          <p><strong>Ya existen en Firestore:</strong> ${csvData.length - missing.length}</p>
          <p><strong>Faltan en Firestore:</strong> ${missing.length}</p>
          ${missing.length > 0 ? '<p class="text-warning">Estos usuarios necesitan documento en Firestore</p>' : ''}
        `,
        confirmButtonColor: '#3085d6'
      });

    } catch (error) {
      console.error('Error analizando usuarios:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Error al analizar usuarios',
        confirmButtonColor: '#d33'
      });
    } finally {
      setAnalyzing(false);
    }
  };

  // Crear documento en Firestore para un usuario
  const createFirestoreDoc = async (userData: MissingUser): Promise<{ success: boolean; message: string }> => {
    try {
      const collectionName = userData.tipo;

      if (userData.tipo === 'asociado') {
        const asociadoData = {
          tipo: 'asociado',
          nombre: userData.nombre,
          nombreEnFlex: userData.nombreCompleto,
          correo: userData.correo,
          telefono: userData.telefono,
          sitio_web: userData.sitioWeb,
          inmobiliaria_doc_id: userData.inmobiliariaDocId,
          certificaciones: userData.certificaciones,
          activo: true,
          calificacion: 0,
          nivel_calificacion: 'Nuevo',
          biografia: '',
          anos_experiencia: 0,
          foto: '',
          breakdown_rating: {
            cursos: 0,
            experiencia: 0,
            opiniones: 0,
            participacion: 0,
            perfil: 0
          },
          redes: userData.redes,
          fecha_creacion: serverTimestamp()
        };

        await addDoc(collection(db, 'asociado'), asociadoData);
      } else {
        // Asesor (Afiliado)
        const asesorData = {
          tipo: 'asesor',
          nombre: userData.nombre,
          nombreEnFlex: userData.nombreCompleto,
          correo: userData.correo,
          telefono: userData.telefono,
          sitio_web: userData.sitioWeb,
          inmobiliaria_doc_ids: userData.inmobiliariaDocId ? [userData.inmobiliariaDocId] : [],
          certificaciones: userData.certificaciones,
          activo: true,
          calificacion: 0,
          biografia: '',
          anos_experiencia: 0,
          foto: '',
          redes: userData.redes,
          fecha_creacion: serverTimestamp()
        };

        await addDoc(collection(db, 'asesor'), asesorData);
      }

      return { success: true, message: `Documento creado en ${collectionName}` };
    } catch (error: any) {
      return { success: false, message: error.message };
    }
  };

  // Crear documentos para todos los usuarios faltantes
  const createAllMissingDocs = async () => {
    if (missingUsers.length === 0) {
      Swal.fire({
        icon: 'info',
        title: 'Sin usuarios pendientes',
        text: 'No hay usuarios faltantes para crear',
        confirmButtonColor: '#3085d6'
      });
      return;
    }

    const confirm = await Swal.fire({
      icon: 'question',
      title: '¿Crear documentos?',
      html: `
        <p>Se crearán <strong>${missingUsers.length}</strong> documentos en Firestore.</p>
        <p class="text-muted">Esto NO crea cuentas en Firebase Auth, solo documentos en Firestore.</p>
      `,
      showCancelButton: true,
      confirmButtonText: 'Sí, crear',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33'
    });

    if (!confirm.isConfirmed) return;

    setLoading(true);
    setProgress({ current: 0, total: missingUsers.length });
    const results: { correo: string; success: boolean; message: string }[] = [];

    for (let i = 0; i < missingUsers.length; i++) {
      const userData = missingUsers[i];
      const result = await createFirestoreDoc(userData);
      results.push({ correo: userData.correo, ...result });
      setProgress({ current: i + 1, total: missingUsers.length });

      // Pequeña pausa para no saturar Firebase
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    setCreationResults(results);
    setLoading(false);

    const exitosos = results.filter(r => r.success).length;
    const fallidos = results.filter(r => !r.success).length;

    Swal.fire({
      icon: exitosos > 0 ? 'success' : 'error',
      title: 'Proceso completado',
      html: `
        <p><strong>Exitosos:</strong> ${exitosos}</p>
        <p><strong>Fallidos:</strong> ${fallidos}</p>
      `,
      confirmButtonColor: '#3085d6'
    });

    // Limpiar lista de faltantes después de crear
    if (exitosos > 0) {
      setMissingUsers([]);
    }
  };

  // Crear documento para un usuario específico (Miriam)
  const createSingleUser = async () => {
    const { value: formValues } = await Swal.fire({
      title: 'Crear usuario manualmente',
      html: `
        <input id="swal-correo" class="swal2-input" placeholder="Correo" value="miriam@gugorealestate.com">
        <input id="swal-nombre" class="swal2-input" placeholder="Nombre" value="Miriam Gonzalez">
        <input id="swal-nombreCompleto" class="swal2-input" placeholder="Nombre completo" value="Miriam Alejandra Gonzalez Gonzalez">
        <input id="swal-telefono" class="swal2-input" placeholder="Teléfono" value="523221060896">
        <input id="swal-sitioWeb" class="swal2-input" placeholder="Sitio web" value="https://gugorealestate.com/">
        <input id="swal-inmobiliariaDocId" class="swal2-input" placeholder="Inmobiliaria Doc ID" value="x5uxbDuJ9ydhecTyVliB">
        <input id="swal-certificaciones" class="swal2-input" placeholder="Certificaciones (separadas por coma)" value="AMPI, NAR">
        <select id="swal-tipo" class="swal2-select">
          <option value="asociado">Asociado</option>
          <option value="asesor">Afiliado (Asesor)</option>
        </select>
      `,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: 'Crear',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#3085d6',
      preConfirm: () => {
        return {
          correo: (document.getElementById('swal-correo') as HTMLInputElement).value,
          nombre: (document.getElementById('swal-nombre') as HTMLInputElement).value,
          nombreCompleto: (document.getElementById('swal-nombreCompleto') as HTMLInputElement).value,
          telefono: (document.getElementById('swal-telefono') as HTMLInputElement).value,
          sitioWeb: (document.getElementById('swal-sitioWeb') as HTMLInputElement).value,
          inmobiliariaDocId: (document.getElementById('swal-inmobiliariaDocId') as HTMLInputElement).value,
          certificaciones: (document.getElementById('swal-certificaciones') as HTMLInputElement).value,
          tipo: (document.getElementById('swal-tipo') as HTMLSelectElement).value
        };
      }
    });

    if (!formValues) return;

    const userData: MissingUser = {
      correo: formValues.correo.toLowerCase().trim(),
      nombre: formValues.nombre,
      nombreCompleto: formValues.nombreCompleto,
      tipo: formValues.tipo as 'asociado' | 'asesor',
      telefono: formValues.telefono,
      sitioWeb: formValues.sitioWeb,
      agenciaNombre: '',
      inmobiliariaDocId: formValues.inmobiliariaDocId || null,
      certificaciones: formValues.certificaciones.split(',').map((c: string) => c.trim()).filter((c: string) => c),
      redes: {
        facebook: 'https://www.facebook.com/MiriamGugoRealEstate',
        instagram: 'https://www.instagram.com/miriamgugorealestate/'
      }
    };

    setLoading(true);
    const result = await createFirestoreDoc(userData);
    setLoading(false);

    Swal.fire({
      icon: result.success ? 'success' : 'error',
      title: result.success ? 'Usuario creado' : 'Error',
      text: result.message,
      confirmButtonColor: result.success ? '#3085d6' : '#d33'
    });
  };

  // Verificar permisos
  if (!user?.tipo?.includes('admin')) {
    return (
      <div className="container py-5">
        <div className="alert alert-danger">
          <h4>Acceso denegado</h4>
          <p>Solo administradores pueden acceder a esta página.</p>
          <button className="btn btn-primary" onClick={() => navigate('/admin-dashboard')}>
            Volver al Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <UserNav />
      <div className="container py-4">
        <Breadcrumb
          items={[
            { label: 'Dashboard', path: '/admin-dashboard' },
            { label: 'Reparar usuarios sin documento Firestore' }
          ]}
        />

        <div className="card shadow-sm mb-4">
          <div className="card-header bg-warning text-dark">
            <h4 className="mb-0">
              <i className="fas fa-tools me-2"></i>
              Reparar Usuarios sin Documento en Firestore
            </h4>
          </div>
          <div className="card-body">
            <div className="alert alert-info">
              <h5><i className="fas fa-info-circle me-2"></i>¿Qué hace esta herramienta?</h5>
              <p className="mb-0">
                Detecta usuarios que existen en Firebase Authentication pero <strong>no tienen documento</strong> en
                las colecciones <code>asociado</code> o <code>asesor</code> de Firestore.
                Esto causa que el sistema les asigne rol <strong>PUBLICO</strong> aunque tengan cuenta.
              </p>
            </div>

            {/* Acciones rápidas */}
            <div className="row mb-4">
              <div className="col-md-6">
                <div className="card border-primary h-100">
                  <div className="card-body">
                    <h5 className="card-title text-primary">
                      <i className="fas fa-user-plus me-2"></i>
                      Crear usuario individual
                    </h5>
                    <p className="card-text">
                      Crea manualmente un documento para un usuario específico (ej: Miriam González)
                    </p>
                    <button
                      className="btn btn-primary"
                      onClick={createSingleUser}
                      disabled={loading}
                    >
                      <i className="fas fa-plus me-2"></i>
                      Crear usuario manual
                    </button>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="card border-secondary h-100">
                  <div className="card-body">
                    <h5 className="card-title text-secondary">
                      <i className="fas fa-file-csv me-2"></i>
                      Importar desde CSV
                    </h5>
                    <p className="card-text">
                      Analiza el archivo CSV de miembros y crea documentos faltantes
                    </p>
                    <input
                      type="file"
                      accept=".csv"
                      onChange={handleFileUpload}
                      className="form-control mb-2"
                    />
                    <small className="text-muted">
                      Usa: miembros-ampi - miembros-ampi-procesado.csv
                    </small>
                  </div>
                </div>
              </div>
            </div>

            {/* CSV cargado */}
            {csvData.length > 0 && (
              <div className="alert alert-success">
                <i className="fas fa-check-circle me-2"></i>
                CSV cargado: <strong>{csvData.length}</strong> registros
                <button
                  className="btn btn-sm btn-outline-success ms-3"
                  onClick={analyzeUsers}
                  disabled={analyzing}
                >
                  {analyzing ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Analizando...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-search me-2"></i>
                      Analizar usuarios faltantes
                    </>
                  )}
                </button>
              </div>
            )}

            {/* Usuarios faltantes */}
            {missingUsers.length > 0 && (
              <div className="card border-warning mb-4">
                <div className="card-header bg-warning text-dark d-flex justify-content-between align-items-center">
                  <span>
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    Usuarios sin documento en Firestore: <strong>{missingUsers.length}</strong>
                  </span>
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={createAllMissingDocs}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Creando {progress.current}/{progress.total}...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-plus-circle me-2"></i>
                        Crear todos los documentos
                      </>
                    )}
                  </button>
                </div>
                <div className="card-body" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  <table aria-label="Tabla de Fix Missing Firestore Users" className="table table-sm table-striped">
                    <thead className="sticky-top bg-white">
                      <tr>
                        <th scope="col">Nombre</th>
                        <th scope="col">Correo</th>
                        <th scope="col">Tipo</th>
                        <th scope="col">Agencia</th>
                        <th scope="col">Inmobiliaria Doc ID</th>
                      </tr>
                    </thead>
                    <tbody>
                      {missingUsers.map((u, idx) => (
                        <tr key={idx}>
                          <td>{u.nombre}</td>
                          <td><code>{u.correo}</code></td>
                          <td>
                            <span className={`badge ${u.tipo === 'asociado' ? 'bg-success' : 'bg-info'}`}>
                              {u.tipo === 'asociado' ? 'Asociado' : 'Afiliado'}
                            </span>
                          </td>
                          <td>{u.agenciaNombre || '-'}</td>
                          <td>
                            {u.inmobiliariaDocId ? (
                              <code className="text-success">{u.inmobiliariaDocId}</code>
                            ) : (
                              <span className="text-danger">No encontrada</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Resultados de creación */}
            {creationResults.length > 0 && (
              <div className="card border-info">
                <div className="card-header bg-info text-white">
                  <i className="fas fa-clipboard-list me-2"></i>
                  Resultados de creación
                </div>
                <div className="card-body" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  <table aria-label="Tabla de Fix Missing Firestore Users" className="table table-sm">
                    <thead>
                      <tr>
                        <th scope="col">Correo</th>
                        <th scope="col">Estado</th>
                        <th scope="col">Mensaje</th>
                      </tr>
                    </thead>
                    <tbody>
                      {creationResults.map((r, idx) => (
                        <tr key={idx} className={r.success ? 'table-success' : 'table-danger'}>
                          <td><code>{r.correo}</code></td>
                          <td>
                            {r.success ? (
                              <i className="fas fa-check-circle text-success"></i>
                            ) : (
                              <i className="fas fa-times-circle text-danger"></i>
                            )}
                          </td>
                          <td>{r.message}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Info adicional */}
        <div className="card">
          <div className="card-header">
            <i className="fas fa-lightbulb me-2"></i>
            Información útil
          </div>
          <div className="card-body">
            <h6>Inmobiliarias cargadas: {inmobiliarias.length}</h6>
            <p className="text-muted mb-0">
              El sistema buscará automáticamente la inmobiliaria por nombre para asignar el <code>inmobiliaria_doc_id</code>.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default FixMissingFirestoreUsers;
